import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import {
  ClientUser,
  ClientUsersData,
  CreateClientUserPayload,
  RangeRep,
  TimeRange
} from '../types/superadmin';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MetricBar,
  RepCell,
  ScoreCell
} from './primitives';
import { fmtInt, initialsOf } from '../lib/format';
import { rangeLabel } from './RangeControl';
import { PRIMARY_RUBRIC_DIMENSIONS, RUBRIC_DIMENSION_KEYS } from '../config/rubricDimensions';
import AddClientUserForm from './AddClientUserForm';
import RepSeriesPanel from './RepSeriesPanel';

/**
 * The client's people, as ONE table.
 *
 * This used to be two: a live roster (add/remove, role, status) and a separate
 * range-scoped reps stack rank. They listed the same human beings against two
 * different sources, in two different orders, so answering "is this rep
 * active, and are they any good?" meant reading both and matching by eye.
 *
 * The merged table keeps both sources and their different freshness:
 *   - ROWS come from the client's LIVE database. That is the authority on who
 *     exists — it includes deactivated users and anyone created since the last
 *     rollup, neither of which the snapshot knows about.
 *   - METRICS come from the nightly rollup, joined on `users.firebaseUid` ==
 *     `rep_daily.userId` (the same key throughout the rollup).
 *
 * A rep with no live user (their account was deleted, their calls were not)
 * still gets a row so their activity is not silently dropped from the totals
 * above.
 *
 * Adding and removing users stays restricted to clients with trial access —
 * they have unlimited seats, so no subscription seat has to be bought or
 * released. Every other client shows the roster read-only, with the reason.
 */

type RepColKind = 'int' | 'minutes' | 'bar' | 'score';
type MetricSortKey = keyof RangeRep;
type SortKey = 'name' | MetricSortKey;

// Sessions, Minutes, overall Call quality, then the five reported rubric
// dimensions. (Live-intelligence dims + Training are commented out for Lushin.)
// All ten are still scored and stored — see PRIMARY_RUBRIC_DIMENSION_KEYS for
// why only five are columns here.
const METRIC_COLUMNS: Array<{ key: MetricSortKey; label: string; kind: RepColKind }> = [
  { key: 'sessions', label: 'Sessions', kind: 'int' },
  { key: 'minutes', label: 'Minutes', kind: 'minutes' },
  { key: 'callQualityScore', label: 'Call quality', kind: 'bar' },
  ...PRIMARY_RUBRIC_DIMENSIONS.map((d) => ({
    key: d.key as MetricSortKey,
    label: d.short,
    kind: 'score' as RepColKind
  }))
];

// caret/# + User + metrics + the row-action column.
const TABLE_COLS = 2 + METRIC_COLUMNS.length + 1;

/** Every rubric dimension, unscored — for a user with no activity in range. */
const EMPTY_DIMENSIONS = RUBRIC_DIMENSION_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: null }),
  {}
) as Pick<RangeRep, (typeof RUBRIC_DIMENSION_KEYS)[number]>;

/**
 * A zero-activity rep row. The API already pads the roster it knows about, but
 * a user created since the last rollup is in the live roster and in no snapshot
 * at all — without this they would have no row shape to render.
 */
const nullRep = (tenantId: string, userId: string, name: string, email: string | null): RangeRep => ({
  userId,
  tenantId,
  displayName: '',
  name,
  email,
  sessions: 0,
  minutes: 0,
  callQualityScore: null,
  trainingScore: null,
  trainingSessions: 0,
  ...EMPTY_DIMENSIONS
});

/** One row: a live account, its range metrics, or (rarely) only one of the two. */
interface ClientPerson {
  key: string;
  name: string;
  email: string | null;
  /** Null when rollup data exists for someone with no account any more. */
  user: ClientUser | null;
  /** Never null — synthesised with zeroes when there was no activity in range. */
  rep: RangeRep;
}

/** Seat usage line. Trial clients are unlimited, so the ratio is meaningless. */
function SeatSummary({ data }: { data: ClientUsersData }) {
  if (data.trialAccessEnabled) {
    return <span className="chip pos">Trial access — unlimited users</span>;
  }
  if (data.totalUserSeats === null) {
    return <span className="chip">No seat information</span>;
  }
  return (
    <span className="chip">
      {data.usedUserSeats ?? 0} / {data.totalUserSeats} seats used
    </span>
  );
}

export default function ClientUsersPanel({
  tenantId,
  clientName,
  reps,
  range
}: {
  tenantId: string;
  clientName: string;
  /** Range-scoped rollup metrics for this client, from the drill-down payload. */
  reps: RangeRep[];
  range: TimeRange;
}) {
  const [data, setData] = useState<ClientUsersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingRemovalId, setPendingRemovalId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('callQualityScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.getClientUsers(tenantId);
      setData(res?.data ?? null);
    } catch (error: any) {
      setLoadError(error?.message || 'Could not load users for this client.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (payload: CreateClientUserPayload) => {
    setSubmitting(true);
    setActionError(null);
    setNotice(null);
    try {
      await api.createClientUser(tenantId, payload);
      setNotice(`${payload.name} can now sign in at ${clientName}.`);
      setShowForm(false);
      await load();
    } catch (error: any) {
      setActionError(error?.message || 'Could not create the user.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (user: ClientUser) => {
    setRemovingId(user.id);
    setActionError(null);
    setNotice(null);
    try {
      await api.deactivateClientUser(tenantId, user.id);
      setNotice(`${user.name} has been deactivated and no longer has access.`);
      setPendingRemovalId(null);
      await load();
    } catch (error: any) {
      setActionError(error?.message || 'Could not deactivate the user.');
    } finally {
      setRemovingId(null);
    }
  };

  // Join the live roster to the range metrics, then append any rollup-only reps.
  const people = useMemo<ClientPerson[]>(() => {
    const repByUserId = new Map(reps.map((r) => [r.userId, r]));
    const users = data?.users ?? [];

    const fromRoster: ClientPerson[] = users.map((user) => ({
      key: user.firebaseUid || user.id,
      name: user.name,
      email: user.email,
      user,
      rep:
        repByUserId.get(user.firebaseUid) ??
        nullRep(tenantId, user.firebaseUid, user.name, user.email)
    }));

    const claimed = new Set(users.map((u) => u.firebaseUid));
    const orphans: ClientPerson[] = reps
      .filter((r) => !claimed.has(r.userId))
      .map((rep) => ({
        key: rep.userId,
        name: rep.name,
        email: rep.email,
        user: null,
        rep
      }));

    return [...fromRoster, ...orphans];
  }, [data, reps, tenantId]);

  const sorted = useMemo(() => {
    const direction = sortDir === 'desc' ? -1 : 1;
    return [...people].sort((a, b) => {
      if (sortKey === 'name') {
        return a.name.localeCompare(b.name) * direction;
      }
      const av = a.rep[sortKey] as number | null;
      const bv = b.rep[sortKey] as number | null;
      // Unscored always sinks, whichever way the column is sorted — a null is
      // "no data", not a low score, and floating it to the top would read as one.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * direction;
    });
  }, [people, sortKey, sortDir]);

  const setSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
      return;
    }
    setSortKey(key);
    // Names read best A→Z; every metric reads best best-first.
    setSortDir(key === 'name' ? 'asc' : 'desc');
  };
  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'desc' ? '↓' : '↑') : '↕');
  const thCls = (key: SortKey) => 'sortable' + (sortKey === key ? ' sorted' : '');

  const canManage = data?.trialAccessEnabled === true;

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h3>Users · {rangeLabel(range)}</h3>
          <p className="desc">
            Live roster from the client&rsquo;s own database, with each user&rsquo;s activity and
            Lushin Discovery Call Rubric scores for the selected period. Click a row for their
            account details, activity by day and individual calls. Sortable.
          </p>
        </div>
        <div className="right">
          {data && <SeatSummary data={data} />}
          {canManage && !showForm && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setShowForm(true);
                setActionError(null);
                setNotice(null);
              }}
            >
              Add user
            </button>
          )}
        </div>
      </div>

      {loading && <LoadingState label="Loading users…" />}
      {!loading && loadError && <ErrorState message={loadError} />}

      {!loading && !loadError && data && (
        <>
          {!canManage && (
            <div className="panel-note">
              This client does not have trial access, so its users are managed from its own admin
              portal against its purchased seats. Enable trial access to manage users from here.
            </div>
          )}

          {notice && <div className="panel-note pos">{notice}</div>}
          {actionError && <div className="panel-note neg">{actionError}</div>}

          {showForm && canManage && (
            <AddClientUserForm
              clientName={clientName}
              submitting={submitting}
              onSubmit={handleCreate}
              onCancel={() => setShowForm(false)}
            />
          )}

          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th className={thCls('name')} onClick={() => setSort('name')}>
                    User <span className="sort-arr">{arrow('name')}</span>
                  </th>
                  {METRIC_COLUMNS.map((col) => (
                    <th key={col.key} className={thCls(col.key)} onClick={() => setSort(col.key)}>
                      {col.label} <span className="sort-arr">{arrow(col.key)}</span>
                    </th>
                  ))}
                  <th style={{ width: 190 }} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((person, index) => {
                  const isOpen = expandedKey === person.key;
                  const { user, rep } = person;
                  const isRemovable = canManage && !!user && user.isActive;

                  return (
                    <React.Fragment key={person.key}>
                      <tr
                        className="clickable"
                        onClick={() => setExpandedKey((cur) => (cur === person.key ? null : person.key))}
                      >
                        <td>
                          <span className="mono" style={{ color: 'var(--ink-3)' }}>
                            <span className={'caret' + (isOpen ? ' open' : '')}>▸</span>
                            {String(index + 1).padStart(2, '0')}
                          </span>
                        </td>
                        <td>
                          <RepCell
                            initials={initialsOf(person.name)}
                            name={person.name}
                            role={person.email || undefined}
                          />
                          <div className="user-chips">
                            {user ? (
                              <>
                                <span className={'chip ' + (user.role === 'admin' ? 'info' : '')}>
                                  {user.role}
                                </span>
                                <span className={'chip ' + (user.isActive ? 'pos' : 'neg')}>
                                  {user.isActive ? 'active' : 'deactivated'}
                                </span>
                              </>
                            ) : (
                              <span
                                className="chip warn"
                                title="This user has activity in the rollup but no account in the client's database any more."
                              >
                                no account
                              </span>
                            )}
                          </div>
                        </td>
                        {METRIC_COLUMNS.map((col) => {
                          const value = rep[col.key] as number | null;
                          return (
                            <td key={col.key}>
                              {col.kind === 'int' && <span className="metric-num">{fmtInt(value ?? 0)}</span>}
                              {col.kind === 'minutes' && (
                                <span className="metric-num">
                                  {fmtInt(value ?? 0)}
                                  <span className="u">min</span>
                                </span>
                              )}
                              {col.kind === 'bar' && <MetricBar value={value} />}
                              {col.kind === 'score' && <ScoreCell value={value} />}
                            </td>
                          );
                        })}
                        {/* Row actions must not toggle the row open underneath them. */}
                        <td onClick={(event) => event.stopPropagation()}>
                          {user && isRemovable && pendingRemovalId !== user.id && (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setPendingRemovalId(user.id)}
                            >
                              Remove
                            </button>
                          )}
                          {user && pendingRemovalId === user.id && (
                            <span className="row-confirm">
                              <span className="caption">Remove access?</span>
                              <button
                                type="button"
                                className="btn btn-danger"
                                disabled={removingId === user.id}
                                onClick={() => handleRemove(user)}
                              >
                                {removingId === user.id ? 'Removing…' : 'Confirm'}
                              </button>
                              <button
                                type="button"
                                className="btn"
                                disabled={removingId === user.id}
                                onClick={() => setPendingRemovalId(null)}
                              >
                                Cancel
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="rep-detail-row">
                          <td colSpan={TABLE_COLS}>
                            <RepSeriesPanel rep={rep} user={user} range={range} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={TABLE_COLS}>
                      <EmptyState>This client has no users yet.</EmptyState>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
